"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Check, Crown, Gavel, X } from "lucide-react";
import { Avatar, Loading, StatePanel } from "@/features/home-v2/ui";
import { useAuthStore } from "@/store/useAuthStore";
import type { BidDecision } from "@/features/bids/useBidDecision";
import type { BidItem } from "@/features/bids/types";
import type { AuctionItem } from "../../types";
import { Panel } from "./AuctionFacts";
import BidDecisionDialog from "./BidDecisionDialog";
import { BID_STATUS, formatDateTime, formatPrice } from "./format";
import type { useAuctionPage } from "./useAuctionPage";
type Page = ReturnType<typeof useAuctionPage>;

/**
 * Every bid on the lot, highest first — AuctionBidsPanel's board, unchanged in
 * what it decides: a failed read is a failure with a retry, never "no bids";
 * the owner accepts or rejects a PENDING bid and names an ACCEPTED bid the
 * winner (while there is none) — only while the auction is published and
 * active; each decision is a reviewed, recorded flow (`useBidDecision`).
 */
export default function AuctionBoard(t0) {
  const $ = _c(86);
  const {
    auction,
    p,
    lang
  } = t0;
  const viewer = useAuthStore(_temp);
  const isFa = lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const router = useRouter();
  const [decision, setDecision] = useState(null);
  let t2;
  if ($[2] !== auction) {
    t2 = b => (b.currency?.symbol || auction.currency?.symbol || "").toUpperCase();
    $[2] = auction;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const symbolOf = t2;
  const closed = p.phase === "ended" || p.phase === "canceled";
  const canOwnerManage = p.isOwner && auction.is_published && auction.status === "active" && !p.settlingNow;
  let t3;
  if ($[4] !== decision?.kind || $[5] !== p || $[6] !== router) {
    t3 = async () => {
      await p.refreshBids();
      await p.board.refetch();
      if (decision?.kind === "set-winner") {
        router.refresh();
      }
    };
    $[4] = decision?.kind;
    $[5] = p;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const refresh = t3;
  let body;
  if (p.boardLoading) {
    let t4;
    if ($[8] !== t) {
      t4 = t("\u062F\u0631 \u062D\u0627\u0644 \u062F\u0631\u06CC\u0627\u0641\u062A \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627\u2026", "Loading bids\u2026");
      $[8] = t;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t4) {
      t5 = <Loading label={t4} className="as-state" />;
      $[10] = t4;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    body = t5;
  } else {
    if (p.boardFailed) {
      let t4;
      if ($[12] !== t) {
        t4 = t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627 \u062F\u0631\u06CC\u0627\u0641\u062A \u0646\u0634\u062F", "Couldn't load the bids");
        $[12] = t;
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      let t5;
      if ($[14] !== t) {
        t5 = t("\u0627\u06CC\u0646 \u06CC\u0639\u0646\u06CC \u062F\u0631\u06CC\u0627\u0641\u062A \u0646\u0627\u0645\u0648\u0641\u0642 \u0628\u0648\u062F\u060C \u0646\u0647 \u0627\u06CC\u0646\u200C\u06A9\u0647 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u0646\u06CC\u0633\u062A.", "This means the read failed \u2014 not that there are no bids.");
        $[14] = t;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      let t6;
      if ($[16] !== p.board) {
        t6 = () => void p.board.refetch();
        $[16] = p.board;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      let t7;
      if ($[18] !== t) {
        t7 = t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again");
        $[18] = t;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      let t8;
      if ($[20] !== t6 || $[21] !== t7) {
        t8 = <button type="button" className="v2-btn v2-btn--ghost v2-btn--sm v2-focus" onClick={t6}>{t7}</button>;
        $[20] = t6;
        $[21] = t7;
        $[22] = t8;
      } else {
        t8 = $[22];
      }
      let t9;
      if ($[23] !== t4 || $[24] !== t5 || $[25] !== t8) {
        t9 = <StatePanel alert={true} title={t4} body={t5} action={t8} />;
        $[23] = t4;
        $[24] = t5;
        $[25] = t8;
        $[26] = t9;
      } else {
        t9 = $[26];
      }
      body = t9;
    } else {
      if (p.bids.length === 0) {
        let t4;
        if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <Gavel aria-hidden={true} />;
          $[27] = t4;
        } else {
          t4 = $[27];
        }
        let t5;
        if ($[28] !== closed || $[29] !== t) {
          t5 = closed ? t("\u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u062B\u0628\u062A \u0646\u0634\u062F", "No bids were placed") : t("\u0647\u0646\u0648\u0632 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u06CC \u062B\u0628\u062A \u0646\u0634\u062F\u0647", "No bids yet");
          $[28] = closed;
          $[29] = t;
          $[30] = t5;
        } else {
          t5 = $[30];
        }
        let t6;
        if ($[31] !== t5) {
          t6 = <b>{t5}</b>;
          $[31] = t5;
          $[32] = t6;
        } else {
          t6 = $[32];
        }
        let t7;
        if ($[33] !== auction || $[34] !== closed || $[35] !== isFa || $[36] !== t) {
          t7 = closed ? null : <p>{t(`حداقل پیشنهاد ${formatPrice(auction.min_price, isFa)} ${(auction.currency?.symbol || "").toUpperCase()} است.`, `The minimum bid is ${formatPrice(auction.min_price, isFa)} ${(auction.currency?.symbol || "").toUpperCase()}.`)}</p>;
          $[33] = auction;
          $[34] = closed;
          $[35] = isFa;
          $[36] = t;
          $[37] = t7;
        } else {
          t7 = $[37];
        }
        let t8;
        if ($[38] !== t6 || $[39] !== t7) {
          t8 = <div className="as-empty">{t4}{t6}{t7}</div>;
          $[38] = t6;
          $[39] = t7;
          $[40] = t8;
        } else {
          t8 = $[40];
        }
        body = t8;
      } else {
        let t4;
        if ($[41] !== canOwnerManage || $[42] !== isFa || $[43] !== lang || $[44] !== p.bids || $[45] !== p.settlement || $[46] !== p.viewerId || $[47] !== p.winnerBid || $[48] !== setDecision || $[49] !== symbolOf || $[50] !== t || $[51] !== viewer?.name || $[52] !== viewer?.username) {
          let t5;
          if ($[54] !== canOwnerManage || $[55] !== isFa || $[56] !== lang || $[57] !== p.settlement || $[58] !== p.viewerId || $[59] !== p.winnerBid || $[60] !== setDecision || $[61] !== symbolOf || $[62] !== t || $[63] !== viewer?.name || $[64] !== viewer?.username) {
            t5 = (bid, i) => {
              const mine = p.viewerId === Number(bid.user_id ?? bid.user?.id ?? 0);
              const name = bid.user?.name || bid.user?.username || (mine ? viewer?.name || viewer?.username : null) || t("\u06A9\u0627\u0631\u0628\u0631", "User");
              const handle = bid.user?.username || (mine ? viewer?.username : null) || null;
              const status = BID_STATUS[bid.status];
              const canModerate = canOwnerManage && bid.status === "pending";
              const canSetWinner = canOwnerManage && bid.status === "accepted" && (!p.winnerBid || p.settlement.state === "failed" && p.winnerBid.id === bid.id);
              return <li key={bid.id} data-win={bid.is_win || undefined}><Avatar name={name} src={bid.user?.profile?.cover} size={36} /><span className="as-bid-who">{handle ? <Link href={`/${lang}/artists/${encodeURIComponent(handle)}`} className="as-bid-name v2-focus"><b>{name}</b></Link> : <b>{name}</b>}<small>{formatDateTime(bid.created_at, isFa)}</small><span className="as-bid-tags">{i === 0 ? <i data-tone="top">{t("\u0628\u0627\u0644\u0627\u062A\u0631\u06CC\u0646", "Top bid")}</i> : null}{mine ? <i data-tone="mine">{t("\u0634\u0645\u0627", "You")}</i> : null}{bid.is_win ? <i data-tone="win"><Crown aria-hidden={true} />{t("\u0628\u0631\u0646\u062F\u0647", "Winner")}</i> : null}</span></span><span className="as-bid-price"><bdi>{formatPrice(bid.price, isFa)}</bdi><small>{symbolOf(bid)}</small></span><span className="as-bid-state" data-status={bid.status}>{status ? isFa ? status[0] : status[1] : bid.status}</span>{canModerate || canSetWinner ? <span className="as-bid-tools">{canModerate ? <><button type="button" className="v2-btn v2-btn--sm v2-focus" onClick={() => setDecision({
                      kind: "accept",
                      bid
                    })}><Check aria-hidden={true} />{t("\u062A\u0623\u06CC\u06CC\u062F", "Accept")}</button><button type="button" className="v2-btn v2-btn--sm v2-btn--ghost as-danger-ghost v2-focus" onClick={() => setDecision({
                      kind: "reject",
                      bid
                    })}><X aria-hidden={true} />{t("\u0631\u062F", "Reject")}</button></> : null}{canSetWinner ? <button type="button" className="v2-btn v2-btn--sm v2-focus" onClick={() => setDecision({
                    kind: "set-winner",
                    bid
                  })}><Crown aria-hidden={true} />{t("\u0627\u0646\u062A\u062E\u0627\u0628 \u0628\u0647\u200C\u0639\u0646\u0648\u0627\u0646 \u0628\u0631\u0646\u062F\u0647", "Set as winner")}</button> : null}</span> : null}</li>;
            };
            $[54] = canOwnerManage;
            $[55] = isFa;
            $[56] = lang;
            $[57] = p.settlement;
            $[58] = p.viewerId;
            $[59] = p.winnerBid;
            $[60] = setDecision;
            $[61] = symbolOf;
            $[62] = t;
            $[63] = viewer?.name;
            $[64] = viewer?.username;
            $[65] = t5;
          } else {
            t5 = $[65];
          }
          t4 = p.bids.map(t5);
          $[41] = canOwnerManage;
          $[42] = isFa;
          $[43] = lang;
          $[44] = p.bids;
          $[45] = p.settlement;
          $[46] = p.viewerId;
          $[47] = p.winnerBid;
          $[48] = setDecision;
          $[49] = symbolOf;
          $[50] = t;
          $[51] = viewer?.name;
          $[52] = viewer?.username;
          $[53] = t4;
        } else {
          t4 = $[53];
        }
        let t5;
        if ($[66] !== t4) {
          t5 = <ol className="as-bids">{t4}</ol>;
          $[66] = t4;
          $[67] = t5;
        } else {
          t5 = $[67];
        }
        body = t5;
      }
    }
  }
  const T0 = Panel;
  const t4 = "as-board-h";
  let t5;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Gavel aria-hidden={true} />;
    $[68] = t5;
  } else {
    t5 = $[68];
  }
  let t6;
  if ($[69] !== t) {
    t6 = t("\u062F\u0641\u062A\u0631 \u067E\u06CC\u0634\u0646\u0647\u0627\u062F\u0647\u0627", "The book of bids");
    $[69] = t;
    $[70] = t6;
  } else {
    t6 = $[70];
  }
  const t7 = nf.format(p.bids.length);
  let t8;
  if ($[71] !== auction || $[72] !== decision || $[73] !== lang || $[74] !== p.record || $[75] !== p.settlement || $[76] !== refresh || $[77] !== setDecision) {
    t8 = decision ? <BidDecisionDialog mode={decision.kind} auction={auction} bid={decision.bid} lang={lang} onClose={() => setDecision(null)} onRecord={p.record} onSuccess={refresh} onQueued={p.settlement.markQueued} /> : null;
    $[71] = auction;
    $[72] = decision;
    $[73] = lang;
    $[74] = p.record;
    $[75] = p.settlement;
    $[76] = refresh;
    $[77] = setDecision;
    $[78] = t8;
  } else {
    t8 = $[78];
  }
  let t9;
  if ($[79] !== T0 || $[80] !== body || $[81] !== t5 || $[82] !== t6 || $[83] !== t7 || $[84] !== t8) {
    t9 = <T0 id={t4} icon={t5} title={t6} count={t7} className="as-board">{body}{t8}</T0>;
    $[79] = T0;
    $[80] = body;
    $[81] = t5;
    $[82] = t6;
    $[83] = t7;
    $[84] = t8;
    $[85] = t9;
  } else {
    t9 = $[85];
  }
  return t9;
}
function _temp(st) {
  return st.user;
}